/**
 * EF-DOCUMENT-REVIEW-UX-AND-DOCUMENT-SHARING-V1: System Contacts Management Page
 * 
 * Allows administrators to manage external contacts for document sharing:
 * - Government agencies
 * - Clients
 * - Venues
 * - Other external organizations
 */

import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Building2,
  User,
  Mail,
  Phone,
  Smartphone,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Loader2,
  Users,
  Landmark,
  MapPin,
  Briefcase,
  RefreshCw,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category configuration
const CATEGORIES = [
  { value: "government", label: "Government Agency", icon: Landmark, color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  { value: "client", label: "Client", icon: Briefcase, color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" },
  { value: "venue", label: "Venue", icon: MapPin, color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  { value: "other", label: "Other", icon: Users, color: "text-purple-400 bg-purple-500/10 border-purple-500/20" },
];

function getCategoryConfig(category) {
  return CATEGORIES.find(c => c.value === category) || CATEGORIES[3];
}

function CategoryBadge({ category }) {
  const config = getCategoryConfig(category);
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`${config.color} text-xs`}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </Badge>
  );
}

// Contact Form Dialog
function ContactFormDialog({ open, onOpenChange, contact, onSave, loading }) {
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    position: "",
    email: "",
    phone: "",
    mobile: "",
    category: "government",
    notes: "",
  });
  
  useEffect(() => {
    if (contact) {
      setFormData({
        company_name: contact.company_name || "",
        contact_name: contact.contact_name || "",
        position: contact.position || "",
        email: contact.email || "",
        phone: contact.phone || "",
        mobile: contact.mobile || "",
        category: contact.category || "government",
        notes: contact.notes || "",
      });
    } else {
      setFormData({
        company_name: "",
        contact_name: "",
        position: "",
        email: "",
        phone: "",
        mobile: "",
        category: "government",
        notes: "",
      });
    }
  }, [contact, open]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }
    
    onSave(formData);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            {contact ? "Edit Contact" : "Add New Contact"}
          </DialogTitle>
          <DialogDescription>
            {contact 
              ? "Update the contact information for document sharing."
              : "Add a new external contact for document sharing."}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Category */}
          <div className="space-y-2">
            <Label>Category *</Label>
            <Select 
              value={formData.category} 
              onValueChange={(val) => setFormData({ ...formData, category: val })}
            >
              <SelectTrigger data-testid="contact-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    <div className="flex items-center gap-2">
                      <cat.icon className="h-4 w-4" />
                      {cat.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Company Name */}
          <div className="space-y-2">
            <Label>Company / Organization Name *</Label>
            <Input
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              placeholder="e.g., DIRECCTE Monaco"
              data-testid="contact-company-name"
            />
          </div>
          
          {/* Contact Name */}
          <div className="space-y-2">
            <Label>Contact Person</Label>
            <Input
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              placeholder="e.g., Jean Dupont"
              data-testid="contact-person-name"
            />
          </div>
          
          {/* Position */}
          <div className="space-y-2">
            <Label>Position / Role</Label>
            <Input
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              placeholder="e.g., HR Manager"
              data-testid="contact-position"
            />
          </div>
          
          {/* Email */}
          <div className="space-y-2">
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="contact@organization.com"
              data-testid="contact-email"
            />
          </div>
          
          {/* Phone Numbers */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+33 1 23 45 67 89"
                data-testid="contact-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Mobile</Label>
              <Input
                type="tel"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                placeholder="+33 6 12 34 56 78"
                data-testid="contact-mobile"
              />
            </div>
          </div>
          
          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional information about this contact..."
              rows={3}
              data-testid="contact-notes"
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="save-contact-btn">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {contact ? "Update Contact" : "Create Contact"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Delete Confirmation Dialog
function DeleteConfirmDialog({ open, onOpenChange, contact, onConfirm, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-400">Delete Contact</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>{contact?.company_name}</strong>? 
            This action cannot be undone and any document sharing history will be preserved.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Delete Contact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Page Component
export default function SystemContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  
  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  
  // Load contacts
  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      const params = categoryFilter !== "all" ? `?category=${categoryFilter}` : "";
      const response = await api.get(`/system-contacts${params}`);
      setContacts(response.data || []);
    } catch (err) {
      console.error("Failed to load contacts:", err);
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);
  
  useEffect(() => {
    loadContacts();
  }, [loadContacts]);
  
  // Filter contacts by search
  const filteredContacts = contacts.filter(contact => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      contact.company_name?.toLowerCase().includes(term) ||
      contact.contact_name?.toLowerCase().includes(term) ||
      contact.email?.toLowerCase().includes(term) ||
      contact.position?.toLowerCase().includes(term)
    );
  });
  
  // Create or update contact
  const handleSaveContact = async (formData) => {
    try {
      setSaving(true);
      
      if (editingContact) {
        // Update
        await api.put(`/system-contacts/${editingContact.id}`, formData);
        toast.success("Contact updated successfully");
      } else {
        // Create
        await api.post("/system-contacts", formData);
        toast.success("Contact created successfully");
      }
      
      setFormDialogOpen(false);
      setEditingContact(null);
      loadContacts();
    } catch (err) {
      console.error("Save contact error:", err);
      toast.error(err.response?.data?.detail || "Failed to save contact");
    } finally {
      setSaving(false);
    }
  };
  
  // Delete contact
  const handleDeleteContact = async () => {
    if (!deletingContact) return;
    
    try {
      setSaving(true);
      await api.delete(`/system-contacts/${deletingContact.id}`);
      toast.success("Contact deleted successfully");
      setDeleteDialogOpen(false);
      setDeletingContact(null);
      loadContacts();
    } catch (err) {
      console.error("Delete contact error:", err);
      toast.error(err.response?.data?.detail || "Failed to delete contact");
    } finally {
      setSaving(false);
    }
  };
  
  // Open edit dialog
  const openEditDialog = (contact) => {
    setEditingContact(contact);
    setFormDialogOpen(true);
  };
  
  // Open create dialog
  const openCreateDialog = () => {
    setEditingContact(null);
    setFormDialogOpen(true);
  };
  
  // Open delete dialog
  const openDeleteDialog = (contact) => {
    setDeletingContact(contact);
    setDeleteDialogOpen(true);
  };
  
  // Category counts
  const categoryCounts = {
    all: contacts.length,
    government: contacts.filter(c => c.category === "government").length,
    client: contacts.filter(c => c.category === "client").length,
    venue: contacts.filter(c => c.category === "venue").length,
    other: contacts.filter(c => c.category === "other").length,
  };
  
  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Building2 className="h-7 w-7 text-primary" />
            System Contacts
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage external contacts for document sharing with government agencies, clients, and venues
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadContacts} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={openCreateDialog} data-testid="add-contact-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Contact
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card 
          className={`cursor-pointer transition-all ${categoryFilter === "all" ? "ring-2 ring-primary" : "hover:bg-muted/30"}`}
          onClick={() => setCategoryFilter("all")}
        >
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">All Contacts</p>
                <p className="text-2xl font-bold">{categoryCounts.all}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        {CATEGORIES.map(cat => (
          <Card 
            key={cat.value}
            className={`cursor-pointer transition-all ${categoryFilter === cat.value ? "ring-2 ring-primary" : "hover:bg-muted/30"}`}
            onClick={() => setCategoryFilter(cat.value)}
          >
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{cat.label}</p>
                  <p className="text-2xl font-bold">{categoryCounts[cat.value]}</p>
                </div>
                <cat.icon className={`h-8 w-8 ${cat.color.split(" ")[0]}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by company, name, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="contact-search"
              />
            </div>
            <span className="text-sm text-muted-foreground">
              {filteredContacts.length} contact{filteredContacts.length !== 1 ? "s" : ""}
            </span>
          </div>
        </CardContent>
      </Card>
      
      {/* Contacts Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Building2 className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No contacts found</p>
              <p className="text-sm">
                {searchTerm ? "Try adjusting your search" : "Add your first contact to get started"}
              </p>
              {!searchTerm && (
                <Button className="mt-4" onClick={openCreateDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contact
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact Person</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContacts.map((contact) => (
                  <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{contact.company_name}</p>
                        {contact.notes && (
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]" title={contact.notes}>
                            {contact.notes}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {contact.contact_name ? (
                        <div>
                          <p>{contact.contact_name}</p>
                          {contact.position && (
                            <p className="text-xs text-muted-foreground">{contact.position}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                        {contact.email}
                      </a>
                    </TableCell>
                    <TableCell>
                      {contact.phone || contact.mobile ? (
                        <div className="space-y-0.5">
                          {contact.phone && (
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              {contact.phone}
                            </div>
                          )}
                          {contact.mobile && (
                            <div className="flex items-center gap-1 text-sm">
                              <Smartphone className="h-3 w-3 text-muted-foreground" />
                              {contact.mobile}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <CategoryBadge category={contact.category} />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(contact)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => openDeleteDialog(contact)}
                            className="text-red-400"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Dialogs */}
      <ContactFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        contact={editingContact}
        onSave={handleSaveContact}
        loading={saving}
      />
      
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        contact={deletingContact}
        onConfirm={handleDeleteContact}
        loading={saving}
      />
    </div>
  );
}
